import { useState } from 'react';
import {
  IconSearch,
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconEye,
  IconStar,
  IconLayoutGrid,
  IconList,
} from '@tabler/icons-react';
import { ActionIcon, Avatar, Badge, Box, Button, Card, Group, Menu, Select, SimpleGrid, Stack, Table, Text, TextInput, Title } from '@/components/ui';
import { mockProducts, Product } from '../mockData/ecommerce';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const ProductCard = ({ product }: { product: Product }) => (
  <Card>
    <Stack gap="sm">
      <Group justify="space-between">
        <Avatar size="lg" radius="md" color="blue">
          {product.name.charAt(0)}
        </Avatar>
        <Menu position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray">
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconEye size={14} />}>View</Menu.Item>
            <Menu.Item leftSection={<IconEdit size={14} />}>Edit</Menu.Item>
            <Menu.Divider />
            <Menu.Item leftSection={<IconTrash size={14} />} color="red">Delete</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <Box>
        <Text fw={600} lineClamp={1}>{product.name}</Text>
        <Text size="xs" c="dimmed" lineClamp={2}>{product.description}</Text>
      </Box>

      <Group justify="space-between">
        <Badge size="sm" variant="light">{product.category}</Badge>
        <Badge
          size="sm"
          color={product.status === 'active' ? 'green' : product.status === 'draft' ? 'yellow' : 'gray'}
        >
          {product.status}
        </Badge>
      </Group>

      <Group justify="space-between">
        <Box>
          <Text size="lg" fw={700}>{formatCurrency(product.price)}</Text>
          {product.compareAtPrice && (
            <Text size="xs" c="dimmed" td="line-through">
              {formatCurrency(product.compareAtPrice)}
            </Text>
          )}
        </Box>
        <Group gap={4}>
          <IconStar size={14} fill="var(--mantine-color-yellow-filled)" color="var(--mantine-color-yellow-filled)" />
          <Text size="sm" fw={500}>{product.rating}</Text>
          <Text size="xs" c="dimmed">({product.reviews})</Text>
        </Group>
      </Group>

      <Group justify="space-between">
        <Text size="sm" c={product.stock > 50 ? 'green' : product.stock > 20 ? 'yellow' : 'red'}>
          {product.stock} in stock
        </Text>
        <Text size="xs" c="dimmed">SKU: {product.sku}</Text>
      </Group>
    </Stack>
  </Card>
);

export default function ProductsPage() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const categories = [...new Set(mockProducts.map(p => p.category))];
  const statuses = ['active', 'draft', 'archived'];

  const filteredProducts = mockProducts.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(search.toLowerCase()) ||
                          product.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !categoryFilter || product.category === categoryFilter;
    const matchesStatus = !statusFilter || product.status === statusFilter;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Products</Title>
          <Text c="dimmed" size="sm">{filteredProducts.length} products found</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>Add Product</Button>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search products..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Category"
            data={categories.map(c => ({ value: c, label: c }))}
            value={categoryFilter}
            onChange={setCategoryFilter}
            clearable
            w={150}
          />
          <Select
            placeholder="Status"
            data={statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={120}
          />
          <Group gap={4}>
            <ActionIcon
              variant={viewMode === 'grid' ? 'filled' : 'light'}
              onClick={() => setViewMode('grid')}
            >
              <IconLayoutGrid size={16} />
            </ActionIcon>
            <ActionIcon
              variant={viewMode === 'list' ? 'filled' : 'light'}
              onClick={() => setViewMode('list')}
            >
              <IconList size={16} />
            </ActionIcon>
          </Group>
        </Group>
      </Card>

      {/* Products */}
      {viewMode === 'grid' ? (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }}>
          {filteredProducts.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </SimpleGrid>
      ) : (
        <Card p={0}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Stock</Table.Th>
                <Table.Th>Rating</Table.Th>
                <Table.Th ta="right">Price</Table.Th>
                <Table.Th w={50}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredProducts.map((product) => (
                <Table.Tr key={product.id}>
                  <Table.Td>
                    <Group gap="sm">
                      <Avatar size="sm" radius="md" color="blue">
                        {product.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Text size="sm" fw={500}>{product.name}</Text>
                        <Text size="xs" c="dimmed">SKU: {product.sku}</Text>
                      </Box>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light">{product.category}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      color={product.status === 'active' ? 'green' : product.status === 'draft' ? 'yellow' : 'gray'}
                    >
                      {product.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={product.stock > 50 ? 'green' : product.stock > 20 ? 'yellow' : 'red'}>
                      {product.stock}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <IconStar size={14} fill="var(--mantine-color-yellow-filled)" color="var(--mantine-color-yellow-filled)" />
                      <Text size="sm">{product.rating}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>{formatCurrency(product.price)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray">
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={<IconEye size={14} />}>View</Menu.Item>
                        <Menu.Item leftSection={<IconEdit size={14} />}>Edit</Menu.Item>
                        <Menu.Divider />
                        <Menu.Item leftSection={<IconTrash size={14} />} color="red">Delete</Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}
    </Stack>
  );
}
